import type { GestureInput, NoteIntent } from "./types";
import type { Instrument, InstrumentVariant, Tuning } from "../data/instruments";
import { getVariantById } from "../data/instruments";
import { noteNameToFrequency } from "../lib/notes";
import { ArticulationEngine, defaultArticulationControlState, type ArticulationControlState } from "./ArticulationEngine";
import { createDrone } from "./DroneEngine";
import { MAX_ACTIVE_VOICES, type AudioVoice } from "./constants";
import { Mixer } from "./Mixer";
import { PhysicalModelLayer } from "./PhysicalModelLayer";
import { ResonanceLayer } from "./ResonanceLayer";
import { SampleEngine } from "./SampleEngine";
import { Scheduler, scheduleGraceNote } from "./Scheduler";
import { TuningEngine } from "./TuningEngine";

declare global {
  interface Window {
    webkitAudioContext?: typeof AudioContext;
  }
}

function createNoteIntent(gesture: GestureInput): NoteIntent {
  return {
    id: `${gesture.noteName}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    noteName: gesture.noteName,
    velocity: Math.min(1, Math.max(0.05, gesture.velocity)),
    ornament: gesture.ornament,
    createdAt: performance.now()
  };
}

export class TimbroamAudioEngine {
  private context: AudioContext | null = null;
  private readonly tuningEngine = new TuningEngine();
  private readonly articulationEngine = new ArticulationEngine();
  private readonly sampleEngine = new SampleEngine();
  private readonly physicalModelLayer = new PhysicalModelLayer();
  private readonly resonanceLayer = new ResonanceLayer();
  private readonly mixer = new Mixer();
  private readonly scheduler = new Scheduler();
  private readonly voices = new Map<string, AudioVoice>();
  private readonly controlState: ArticulationControlState = { ...defaultArticulationControlState };

  async ensureReady(): Promise<boolean> {
    if (!this.context) {
      const AudioContextConstructor = window.AudioContext ?? window.webkitAudioContext;

      if (!AudioContextConstructor) {
        throw new Error("This browser does not support the Web Audio API.");
      }

      this.context = new AudioContextConstructor();
      this.mixer.connectToOutput(this.context);
    }

    if (this.context.state === "suspended") {
      await this.context.resume();
    }

    return this.context.state === "running";
  }

  get state(): AudioContextState | "unavailable" {
    return this.context?.state ?? "unavailable";
  }

  setControl(controlId: string, value: number | string | boolean): void {
    const numericValue = typeof value === "number" ? Math.min(1, Math.max(0, value)) : value;

    if (controlId === "bow_pressure" && typeof numericValue === "number") {
      this.controlState.bowPressure = numericValue;
    }

    if (controlId === "bow_speed" && typeof numericValue === "number") {
      this.controlState.bowSpeed = numericValue;
    }

    if (
      controlId === "erhu_portamento_shape" &&
      (numericValue === "smoothstep" || numericValue === "logistic")
    ) {
      this.controlState.erhuPortamentoShape = numericValue;
    }

    if (controlId === "breath_pressure" && typeof numericValue === "number") {
      this.controlState.breathPressure = numericValue;
    }

    if (controlId === "cheong_tension" && typeof numericValue === "number") {
      this.controlState.cheongTension = numericValue;
    }

    if (controlId === "cheong_exposure" && typeof numericValue === "number") {
      this.controlState.cheongExposure = numericValue;
    }

    if (controlId === "buzz_amount" && typeof numericValue === "number") {
      this.controlState.buzzAmount = numericValue;
    }

    if (controlId === "bayan_pressure" && typeof numericValue === "number") {
      this.controlState.bayanPressure = numericValue;
    }

    if (controlId === "ney_microbend" && typeof numericValue === "number") {
      this.controlState.neyMicrobend = numericValue;
      this.controlState.microBendAmount = numericValue;
    }

    if (controlId === "mouth_shape_y" && typeof numericValue === "number") {
      this.controlState.mouthShapeY = numericValue;
    }

    if (controlId === "circular_hold" && typeof numericValue === "boolean") {
      this.controlState.circularHold = numericValue;
    }

    if (controlId === "vocalization" && typeof numericValue === "number") {
      this.controlState.vocalization = numericValue;
    }

    if (controlId === "ombak_depth" && typeof numericValue === "number") {
      this.controlState.ombakDepth = numericValue;
    }

    if (
      controlId === "saron_tuning_hint" &&
      (numericValue === "genericSlendroApprox" || numericValue === "genericPelogApprox")
    ) {
      this.controlState.saronTuningHint = numericValue;
    }

    if (controlId === "nonghyeon_depth" && typeof numericValue === "number") {
      this.controlState.nonghyeonDepth = numericValue;
    }

    if (controlId === "micro_bend_amount" && typeof numericValue === "number") {
      this.controlState.microBendAmount = numericValue;
    }

    if (
      controlId === "sitar_meend_mode" &&
      (numericValue === "beginner" || numericValue === "standard" || numericValue === "expert")
    ) {
      this.controlState.meendMode = numericValue;
    }
  }

  async playGesture(
    gesture: GestureInput,
    instrument: Instrument,
    tuning: Tuning,
    variant: InstrumentVariant = getVariantById(instrument.id, instrument.variants[0]?.id ?? "")
  ): Promise<string> {
    await this.ensureReady();

    if (!this.context) {
      throw new Error("Audio context did not initialize.");
    }

    const intent = createNoteIntent(gesture);
    const maqamSnapCents = instrument.id === "oud" ? this.getNearestTuningSnapCents(intent.noteName, tuning) : 0;
    const tuned = this.tuningEngine.resolve(intent, tuning, maqamSnapCents);
    const articulated = this.articulationEngine.shape(tuned, instrument, this.controlState);
    const voice = this.sampleEngine.render(this.context, articulated, instrument, variant, {
      ...this.controlState,
      tremolo: gesture.ornament === "tremolo"
    });
    const mixerOutput = this.mixer.getOutput(this.context);
    const resonator = this.resonanceLayer.connect(this.context, voice.output, mixerOutput, instrument);

    this.physicalModelLayer.apply(this.context, voice, articulated, instrument, variant, {
      controlValue:
        gesture.ornament === "bayan_pressure"
          ? this.controlState.bayanPressure
          : gesture.ornament === "meend"
            ? 500
            : gesture.ornament === "nonghyeon"
              ? this.controlState.nonghyeonDepth
              : gesture.ornament === "micro_bend"
                ? this.controlState.microBendAmount
                : undefined,
      meendMode: this.controlState.meendMode,
      erhuPortamentoShape: this.controlState.erhuPortamentoShape
    });
    voice.cleanupNodes.push(resonator);
    this.registerVoice(voice);

    return voice.id;
  }

  async playControl(
    controlId: string,
    instrument: Instrument,
    tuning: Tuning,
    variant: InstrumentVariant
  ): Promise<string | null> {
    await this.ensureReady();

    if (controlId === "drone_toggle" || controlId === "didgeridoo_drone") {
      return this.toggleDrone(instrument, variant);
    }

    if (controlId === "janggu_both_open") {
      const first = await this.playGesture(
        { source: "button", noteName: "C4", velocity: 0.82, ornament: "gung" },
        instrument,
        tuning,
        variant
      );
      scheduleGraceNote(this.scheduler, 4, () => {
          void this.playMomentaryControl(
            { source: "button", noteName: "G4", velocity: 0.78, ornament: "chaepyeon" },
            instrument,
            tuning,
            variant,
            520
          );
      });

      return first;
    }

    if (controlId === "kora_arpeggio") {
      const notes = ["C4", "E4", "G4", "C5"];
      notes.forEach((noteName, index) => {
        scheduleGraceNote(this.scheduler, index * 72, () => {
          void this.playMomentaryControl(
            { source: "button", noteName, velocity: 0.62, ornament: "arpeggio" },
            instrument,
            tuning,
            variant,
            760
          );
        });
      });

      return null;
    }

    if (controlId === "kora_ostinato") {
      ["C4", "G4", "D4", "G4"].forEach((noteName, index) => {
        scheduleGraceNote(this.scheduler, index * 110, () => {
          void this.playMomentaryControl(
            { source: "button", noteName, velocity: 0.5, ornament: "ostinato" },
            instrument,
            tuning,
            variant,
            820
          );
        });
      });

      return null;
    }

    if (controlId === "sitar_chikari") {
      return this.playMomentaryControl(
        { source: "button", noteName: "C5", velocity: 0.58, ornament: "chikari" },
        instrument,
        tuning,
        variant,
        920
      );
    }

    return null;
  }

  stopVoice(id: string): void {
    const voice = this.voices.get(id);

    if (!voice || !this.context) {
      return;
    }

    voice.stop(this.context.currentTime);
    this.cleanupVoice(id, voice, 1200);
  }

  stopAll(): void {
    this.scheduler.cancelAll();

    for (const [id, voice] of this.voices.entries()) {
      voice.stop(this.context?.currentTime);
      this.cleanupVoice(id, voice, 1200);
    }
  }

  private toggleDrone(instrument: Instrument, variant: InstrumentVariant): string | null {
    if (!this.context) {
      return null;
    }

    const existing = Array.from(this.voices.entries()).find(([id]) => id.startsWith("drone-"));

    if (existing) {
      this.stopVoice(existing[0]);
      return null;
    }

    const baseHz = instrument.id === "yidaki_didgeridoo" ? noteNameToFrequency("C2") : noteNameToFrequency("C4");
    const voice = createDrone(this.context, {
      id: `drone-${instrument.id}-${Date.now()}`,
      variantId: variant.id,
      frequency: baseHz,
      gain: instrument.id === "yidaki_didgeridoo" ? 0.34 : 0.16,
      waveform: instrument.id === "yidaki_didgeridoo" ? "sawtooth" : "triangle",
      mouthX: this.controlState.breathPressure,
      mouthY: this.controlState.mouthShapeY || (variant.id === "traditional_yidaki" ? 0.46 : 0.36),
      circularHold: this.controlState.circularHold,
      vocalization: this.controlState.vocalization
    });

    voice.output.connect(this.mixer.getOutput(this.context));
    this.registerVoice(voice);

    return voice.id;
  }

  private getNearestTuningSnapCents(noteName: string, tuning: Tuning): number {
    const cents = tuning.detuneCents[noteName] ?? 0;

    return Math.round(cents / 5) * 5;
  }

  private async playMomentaryControl(
    gesture: GestureInput,
    instrument: Instrument,
    tuning: Tuning,
    variant: InstrumentVariant,
    durationMs: number
  ): Promise<string> {
    const voiceId = await this.playGesture(gesture, instrument, tuning, variant);
    scheduleGraceNote(this.scheduler, durationMs, () => this.stopVoice(voiceId));

    return voiceId;
  }

  private registerVoice(voice: AudioVoice): void {
    if (this.voices.size >= MAX_ACTIVE_VOICES) {
      const oldest = this.voices.entries().next().value as [string, AudioVoice] | undefined;

      if (oldest) {
        this.stopVoice(oldest[0]);
      }
    }

    this.voices.set(voice.id, voice);

    voice.sources[0]?.addEventListener(
      "ended",
      () => {
        this.cleanupVoice(voice.id, voice, 100);
      },
      { once: true }
    );
  }

  private cleanupVoice(id: string, voice: AudioVoice, delayMs: number): void {
    window.setTimeout(() => {
      this.voices.delete(id);

      for (const source of voice.sources) {
        try {
          source.disconnect();
        } catch {
          // Source may already be disconnected by the browser.
        }
      }

      for (const node of [voice.gain, voice.output, ...voice.cleanupNodes]) {
        try {
          node.disconnect();
        } catch {
          // Node cleanup is best-effort after scheduled releases.
        }
      }
    }, delayMs);
  }
}

export const timbroamAudioEngine = new TimbroamAudioEngine();
